import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { formatApiError } from "@/context/AuthContext";
import { ROLE_LABELS, ROLE_COLORS } from "@/lib/steps";
import { toast } from "sonner";
import { UserPlus, Trash2, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// All roles a user can hold — a user may be assigned any combination of these
// (e.g. both "bd" and "finance"), not just one.
const ALL_ROLES = ["admin", "bd", "pm", "it", "finance", "hr", "client", "faculty"];

function RoleCheckboxes({ value, onChange, testIdPrefix }) {
  const toggle = (r) => onChange(value.includes(r) ? value.filter((x) => x !== r) : [...value, r]);
  return (
    <div className="border border-border rounded-md p-3 grid grid-cols-2 gap-2">
      {ALL_ROLES.map((r) => (
        <label key={r} className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            data-testid={`${testIdPrefix}-${r}`}
            checked={value.includes(r)}
            onChange={() => toggle(r)}
            className="h-4 w-4"
          />
          {ROLE_LABELS[r]}
        </label>
      ))}
    </div>
  );
}

function RoleBadges({ roles }) {
  return (
    <div className="flex flex-wrap gap-1">
      {(roles || []).map((r) => (
        <Badge key={r} variant="outline" className={ROLE_COLORS[r]}>{ROLE_LABELS[r]}</Badge>
      ))}
    </div>
  );
}

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", roles: ["pm"] });
  const [loading, setLoading] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // user being role-edited
  const [editRoles, setEditRoles] = useState([]);
  const [editNotificationEmail, setEditNotificationEmail] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setLoading(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ email: "", password: "", name: "", roles: ["pm"] });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditRoles(u.roles || []);
    setEditNotificationEmail(u.notification_email || "");
  };

  const saveRoles = async () => {
    if (!editTarget) return;
    setEditLoading(true);
    try {
      await api.put(`/users/${editTarget.id}/roles`, { roles: editRoles });
      await api.put(`/users/${editTarget.id}/notification-email`, {
        notification_email: editNotificationEmail.trim() || null,
      });
      toast.success("User updated");
      setEditTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEditLoading(false); }
  };

  return (
    <div className="p-6 sm:p-10 max-w-[1200px] mx-auto page-enter">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Administration</div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight mt-1">Team Members</h1>
          <p className="text-sm text-muted-foreground mt-1">Admin can add users and assign any combination of roles — a user isn't limited to just one.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="au-add-user"><UserPlus className="w-4 h-4 mr-2" />Add User</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create User</DialogTitle></DialogHeader>
            <div className="grid gap-4">
              <div className="space-y-2"><Label>Name</Label><Input data-testid="au-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-2"><Label>Email</Label><Input data-testid="au-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-2"><Label>Password</Label><Input data-testid="au-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div className="space-y-2">
                <Label>Roles</Label>
                <RoleCheckboxes value={form.roles} onChange={(roles) => setForm({ ...form, roles })} testIdPrefix="au-role" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="au-create-submit" onClick={create} disabled={loading || !form.email || !form.password || !form.name || form.roles.length === 0}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mt-8 border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Name</TableHead><TableHead>Login Email</TableHead><TableHead>Notification Email</TableHead><TableHead>Roles</TableHead><TableHead>Created</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.notification_email || "(same as login)"}</TableCell>
                  <TableCell><RoleBadges roles={u.roles} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" data-testid={`edit-roles-${u.id}`} onClick={() => openEdit(u)}><Pencil className="w-4 h-4" /></Button>
                    {u.id !== user.id && (
                      <Button variant="ghost" size="sm" data-testid={`delete-user-${u.id}`} onClick={() => del(u.id)}><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User — {editTarget?.name}</DialogTitle>
            <DialogDescription>Assign any combination of roles, and optionally redirect this user's role-based notification emails without changing their login.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <RoleCheckboxes value={editRoles} onChange={setEditRoles} testIdPrefix="edit-role" />
            <div className="space-y-2">
              <Label>Notification email (optional)</Label>
              <Input
                data-testid="edit-notification-email"
                type="email"
                placeholder={editTarget?.email}
                value={editNotificationEmail}
                onChange={(e) => setEditNotificationEmail(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Where this user's role-based notifications (step handoffs, billing, etc.) are sent. Leave blank to send to their login email ({editTarget?.email}) instead.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button data-testid="save-roles-submit" onClick={saveRoles} disabled={editLoading || editRoles.length === 0}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
